import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Box,
  Container,
  useTheme,
  alpha,
} from "@mui/material";
import {
  QrCode,
  Description,
  BarChart,
  Payment,
  ArrowForward,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import HeaderBar from "../widgets/HeaderBar";
import Footer from "../widgets/Footer";
import { motion } from "framer-motion";
import LayoutContainer from "../widgets/LayoutContainer";

export const features = [
  {
    title: "Custom Registration Forms",
    description:
      "Decide exactly what information you need from your visitors. Tailor forms to your event’s needs.",
  },
  {
    title: "QR Code Generation",
    description:
      "Every visitor gets a unique QR code via email or WhatsApp for seamless event entry.",
  },
  {
    title: "Footfall & Analytics",
    description:
      "Monitor visitor data in real time with insightful reports for better decision-making.",
  },
  {
    title: "Premium Entry & Payments",
    description:
      "Enable paid registrations and validate tickets automatically for a hassle-free event.",
  },
];

const icons = {
  "Custom Registration Forms": <Description fontSize="large" />,
  "QR Code Generation": <QrCode fontSize="large" />,
  "Footfall & Analytics": <BarChart fontSize="large" />,
  "Premium Entry & Payments": <Payment fontSize="large" />,
};

export const FeatureCard = ({ title, description }) => (
  <Card
    sx={{
      boxShadow: 3,
      borderRadius: 3,
      p: 3,
      height: "100%",
      transition: "transform 0.3s ease-in-out",
      "&:hover": {
        transform: "scale(1.05)",
        boxShadow: 6,
      },
    }}
  >
    <CardContent
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        height: "100%",
      }}
    >
      <Box sx={{ mb: 2 }}>{icons[title]}</Box>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" flexGrow={1}>
        {description}
      </Typography>
    </CardContent>
  </Card>
);

const LocFeatureCard = ({ title, description, icon }) => {
  const theme = useTheme();

  return (
    <motion.div
      whileHover={{ y: -10 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Card
        sx={{
          background: alpha(theme.palette.background.paper, 0.8),
          borderRadius: 4,
          p: 3,
          height: "100%",
          transition: "all 0.3s ease",
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
          backdropFilter: "blur(10px)",
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          "&:hover": {
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.2)}`,
          },
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            height: "100%",
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              color: "white",
            }}
          >
            {icon}
          </Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {title}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            {description}
          </Typography>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const FeaturesPage = () => {
  const theme = useTheme();

  return (
    <LayoutContainer>
      <Box
        component="main"
        sx={{
          flex: 1,
          py: 8,
          background: "radial-gradient(circle at top, #f8f9ff, #ffffff)",
        }}
      >
        <Container maxWidth="xl">
          <Box textAlign="center" mb={8}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h2"
                fontWeight={800}
                gutterBottom
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-0.03em",
                }}
              >
                Powerful Event Management Features
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Everything you need to create exceptional event experiences
              </Typography>
            </motion.div>
          </Box>

          <Grid container spacing={4} justifyContent="center" sx={{ mb: 10 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <FeatureCard
                  title={feature.title}
                  description={feature.description}
                  icon={
                    index === 0 ? (
                      <Description fontSize="large" />
                    ) : index === 1 ? (
                      <QrCode fontSize="large" />
                    ) : index === 2 ? (
                      <BarChart fontSize="large" />
                    ) : (
                      <Payment fontSize="large" />
                    )
                  }
                />
              </Grid>
            ))}
          </Grid>

          <Box
            sx={{
              textAlign: "center",
              background: `linear-gradient(45deg, ${alpha(
                theme.palette.primary.main,
                0.1
              )}, ${alpha(theme.palette.secondary.main, 0.1)})`,
              borderRadius: 4,
              p: 6,
              mx: "auto",
              maxWidth: 800,
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Need Custom Solutions?
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
              Our team specializes in building tailored event management
              solutions to match your unique requirements.
            </Typography>
            <Button
              component={Link}
              to="/contactus"
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              sx={{
                px: 6,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 24px ${alpha(
                    theme.palette.primary.main,
                    0.3
                  )}`,
                },
                transition: "all 0.3s ease",
              }}
            >
              Discuss Your Project
            </Button>
          </Box>
        </Container>
      </Box>
    </LayoutContainer>
  );
};

export default FeaturesPage;
